<template>
  <AdminLayout v-if="organization">
    <template #breadcrumb>
      <breadcrumb-link view="AdminOrganizationList" label="Organizations" />
      <breadcrumb-link view="OrganizationDetail" :label="organization.name" :active="true" />
    </template>
    <div class="box" style="position: relative">
      <b-switch
        size="is-medium"
        @input="handleFeature($event)"
        v-model="organization.has_override_reimbursement"
      >
        Reimbursement Estimate Feature Override
      </b-switch>
    </div>

    <div class="box" style="position: relative">
      <b-switch
        size="is-medium"
        @input="handleBlanketVOBOverride($event)"
        v-model="organization.has_override_blanket_vob"
      >
        Blanket VOB Feature Override
      </b-switch>

      <div v-if="organization.has_override_blanket_vob" class="mt-4">
        <p class="help">
          <strong>Started:</strong>
          {{
            organization.override_blanket_vob_starts_at
              ? new Date(organization.override_blanket_vob_starts_at).toLocaleDateString()
              : 'Not set'
          }}
        </p>
        <p class="help" v-if="organization.override_blanket_vob_ends_at">
          <strong>Expires:</strong>
          {{ new Date(organization.override_blanket_vob_ends_at).toLocaleDateString() }}
        </p>
        <p class="help" v-else>
          <strong>Expires:</strong>
          Never
        </p>
        <p class="help">
          <strong>Limit:</strong>
          {{
            organization.override_blanket_vob_limit === 0
              ? 'Unlimited'
              : organization.override_blanket_vob_limit
          }}
        </p>
      </div>
    </div>

    <SubscriptionDetail
      :isForAdmin="true"
      :subscription="currentSubscription"
      :user="organization"
    />

    <div class="box" style="position: relative">
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <h1 class="title">Product Purchases</h1>
          </div>
        </div>
      </div>
      <hr />
      <b-table :data="productPurchases" striped hoverable mobile-cards mobile-breakpoint="1024px">
        <b-table-column field="product" label="Product" v-slot="props">
          {{ productPurchaseName(props.row.product) }}
        </b-table-column>
        <b-table-column field="amount_total" label="Amount Paid" v-slot="props">
          {{ productPurchaseAmount(props.row) }}
        </b-table-column>
        <b-table-column field="status" label="Status" v-slot="props">
          <span
            class="tag"
            :class="props.row.status === 'paid' ? 'is-success is-light' : 'is-light'"
          >
            {{ props.row.status }}
          </span>
        </b-table-column>
        <b-table-column field="purchased_at" label="Purchased" v-slot="props">
          {{ props.row.purchased_at | formatDate }}
        </b-table-column>
        <b-table-column field="stripe_payment_intent_id" label="Payment" v-slot="props">
          <a
            v-if="props.row.stripe_payment_intent_id"
            target="_blank"
            :href="'https://dashboard.stripe.com/payments/' + props.row.stripe_payment_intent_id"
          >
            {{ props.row.stripe_payment_intent_id }}
          </a>
          <span v-else>-</span>
        </b-table-column>
        <b-table-column field="stripe_invoice_id" label="Invoice" v-slot="props">
          <a
            v-if="props.row.stripe_invoice_id"
            target="_blank"
            :href="'https://dashboard.stripe.com/invoices/' + props.row.stripe_invoice_id"
          >
            {{ props.row.stripe_invoice_id }}
          </a>
          <span v-else>-</span>
        </b-table-column>

        <template #empty>
          <section class="section has-text-centered">
            <p>No product purchases.</p>
          </section>
        </template>
      </b-table>
    </div>

    <div class="box" style="position: relative">
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <div>
              <h1 class="title">API Access Keys</h1>
              <p v-if="currentSubscription && currentSubscription.has_api_access" class="help">
                API Access is enabled for this organization.
              </p>
              <p v-else class="help">API Access is not enabled for this organization.</p>
            </div>
          </div>
        </div>
        <div class="level-right">
          <div
            v-if="
              currentSubscription &&
              currentSubscription.is_active &&
              !currentSubscription.has_api_access
            "
            class="level-item"
          >
            <b-button
              type="is-success is-light"
              @click="enableAPIAccess"
              :loading="enablingAPIAccess"
            >
              Enable API Access
            </b-button>
          </div>
          <div class="level-item">
            <b-button
              type="is-primary is-light"
              @click="generateAPIKey"
              :disabled="!currentSubscription || !currentSubscription.has_api_access"
            >
              Generate API Key
            </b-button>
          </div>
        </div>
      </div>
      <hr />
      <b-table :data="apiKeys" striped hoverable mobile-cards mobile-breakpoint="1024px">
        <b-table-column field="name" label="Name" v-slot="props">
          {{ props.row.name }}
        </b-table-column>
        <b-table-column field="key" label="API Key" v-slot="props">
          <span v-if="props.row.key" class="api-key-value">{{ props.row.key }}</span>
          <span v-else>-</span>
        </b-table-column>
        <b-table-column field="key_prefix" label="Prefix" v-slot="props">
          {{ props.row.key_prefix }}
        </b-table-column>
        <b-table-column field="status" label="Status" v-slot="props">
          <span
            class="tag"
            :class="props.row.status === 'active' ? 'is-success is-light' : 'is-light'"
          >
            {{ props.row.status }}
          </span>
        </b-table-column>
        <b-table-column field="created_at" label="Created" v-slot="props">
          {{ props.row.created_at | formatDate }}
        </b-table-column>
        <b-table-column v-slot="props" numeric>
          <b-button
            v-if="props.row.key"
            type="is-primary"
            size="is-small"
            outlined
            @click="copyAPIKey(props.row.key)"
          >
            Copy
          </b-button>
          <b-button
            v-if="props.row.status === 'active'"
            type="is-danger"
            size="is-small"
            outlined
            class="ml-2"
            @click="revokeAPIKey(props.row)"
          >
            Revoke
          </b-button>
        </b-table-column>

        <template #empty>
          <section class="section has-text-centered">
            <p>No API keys issued.</p>
          </section>
        </template>
      </b-table>
    </div>

    <div class="box" style="position: relative">
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <h1 class="title">Subscriptions</h1>
          </div>
        </div>
      </div>
      <hr />
      <div v-if="subscriptions.length > 0">
        <AdminSubscriptionTable :subscriptions="subscriptions" />
      </div>
      <h1 v-else>No Subscriptions</h1>
    </div>

    <AdminOrganizationInviteList :organization="organization" />

    <div class="box" style="position: relative">
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <h1 class="title">Users</h1>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <b-button
              type="is-primary is-light"
              tag="router-link"
              :to="{ name: 'AdminUserInvite' }"
            >
              Invite New User
            </b-button>
          </div>
        </div>
      </div>
      <hr />
      <AdminUserTable :users="users" />
    </div>
    <div class="box" style="position: relative">
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <h1 class="title">Providers</h1>
          </div>
        </div>
      </div>
      <hr />
      <div class="box" style="position: relative">
        <b-switch
          size="is-medium"
          @input="handlePermProviderOverride($event)"
          v-model="organization.perm_provider_override"
          :disabled="perm_provider_override_clicked"
        >
          Organization Providers Override
        </b-switch>
      </div>

      <AdminProviderTable :providers="providers" />
    </div>
  </AdminLayout>
</template>

<script>
import axios from 'axios';
import request from '../../request';

import AdminProviderTable from '../../components/AdminProviderTable.vue';
import AdminUserTable from '../../components/AdminUserTable.vue';
import AdminSubscriptionTable from '../../components/AdminSubscriptionTable.vue';
import AdminLayout from '../../layouts/AdminLayout.vue';
import SubscriptionDetail from '../../components/SubscriptionDetail';
import AdminOrganizationInviteList from './AdminOrganizationInviteList.vue';

export default {
  name: 'AdminOrganizationDetail',
  components: {
    AdminLayout,
    AdminUserTable,
    AdminSubscriptionTable,
    AdminProviderTable,
    SubscriptionDetail,
    AdminOrganizationInviteList,
  },
  data() {
    return {
      loading: false,
      organization: null,
      currentSubscription: null,
      subscriptions: [],
      productPurchases: [],
      apiKeys: [],
      users: [],
      providers: [],
      enablingAPIAccess: false,
      perm_provider_override_clicked: null,
    };
  },
  created() {
    axios
      .all([
        this.getSubscriptions(),
        this.getOrganization(),
        this.getProductPurchases(),
        this.getAPIKeys(),
        this.getUsers(),
        this.getProviders(),
      ])
      .finally(() => (this.loading = false));
  },
  methods: {
    getOrganization() {
      return request.get(`/admin/organizations/${this.$route.params.id}`).then((resp) => {
        this.organization = resp.data.organization;
      });
    },
    getSubscriptions() {
      request
        .get(`/admin/subscriptions?organization_id=${this.$route.params.id}`)
        .then((resp) => (this.subscriptions = resp.data.subscriptions));
      request
        .get(`/admin/organizations/${this.$route.params.id}/current-subscription`)
        .then((resp) => (this.currentSubscription = resp.data.subscription));
    },
    getUsers() {
      request
        .get(`/admin/users?organization_id=${this.$route.params.id}`)
        .then((resp) => (this.users = resp.data.users));
    },
    getProviders() {
      request
        .get(`/admin/providers?organization_id=${this.$route.params.id}`)
        .then((resp) => (this.providers = resp.data.providers));
    },
    getProductPurchases() {
      return request
        .get(`/admin/organizations/${this.$route.params.id}/product-purchases`)
        .then((resp) => (this.productPurchases = resp.data.product_purchases));
    },
    getAPIKeys() {
      return request
        .get(`/admin/organizations/${this.$route.params.id}/api-keys`)
        .then((resp) => (this.apiKeys = resp.data.api_keys));
    },
    productPurchaseName(product) {
      if (product === 'api_access') {
        return 'API Access';
      }
      return product;
    },
    productPurchaseAmount(purchase) {
      if (!purchase.currency) {
        return '-';
      }
      try {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: purchase.currency.toUpperCase(),
        }).format((purchase.amount_total || 0) / 100);
      } catch (e) {
        return `${purchase.amount_total || 0} ${purchase.currency.toUpperCase()}`;
      }
    },
    enableAPIAccess() {
      this.$buefy.dialog.confirm({
        title: 'Enable API Access',
        message:
          '<p>This will enable API access without a Stripe payment and expire any pending API access checkout.</p>',
        confirmText: 'Enable',
        type: 'is-success',
        onConfirm: () => {
          this.enablingAPIAccess = true;
          request
            .post(`/admin/organizations/${this.$route.params.id}/api-access`)
            .then(() => {
              this.$buefy.toast.open({
                message: 'API Access enabled.',
                type: 'is-success',
              });
              return axios.all([
                this.getProductPurchases(),
                request
                  .get(`/admin/organizations/${this.$route.params.id}/current-subscription`)
                  .then((resp) => (this.currentSubscription = resp.data.subscription)),
              ]);
            })
            .catch((error) => {
              this.$buefy.toast.open({
                message: error.response?.data?.error || 'Failed to enable API Access',
                type: 'is-danger',
              });
            })
            .finally(() => {
              this.enablingAPIAccess = false;
            });
        },
      });
    },
    generateAPIKey() {
      this.$buefy.dialog.prompt({
        title: 'Generate API Key',
        message: 'Name this API key.',
        inputAttrs: {
          placeholder: 'Production API Key',
          maxlength: 255,
        },
        confirmText: 'Generate',
        trapFocus: true,
        onConfirm: (name) => {
          this.loading = true;
          request
            .post(`/admin/organizations/${this.$route.params.id}/api-keys`, { name })
            .then(() => {
              this.$buefy.toast.open({
                message: 'API key generated.',
                type: 'is-success',
              });
              return this.getAPIKeys();
            })
            .catch((error) => {
              this.$buefy.toast.open({
                message: error.response?.data?.error || 'Failed to generate API key',
                type: 'is-danger',
              });
            })
            .finally(() => {
              this.loading = false;
            });
        },
      });
    },
    revokeAPIKey(apiKey) {
      this.$buefy.dialog.confirm({
        title: 'Revoke API Key',
        message: `<p>This will revoke "${apiKey.name}".</p>`,
        confirmText: 'Revoke',
        type: 'is-danger',
        onConfirm: () => {
          this.loading = true;
          request
            .post(`/admin/organizations/${this.$route.params.id}/api-keys/${apiKey.id}/revoke`)
            .then(() => {
              this.$buefy.toast.open({
                message: 'API key revoked.',
                type: 'is-success',
              });
              return this.getAPIKeys();
            })
            .finally(() => {
              this.loading = false;
            });
        },
      });
    },
    copyAPIKey(key) {
      if (!navigator.clipboard) {
        return;
      }
      navigator.clipboard.writeText(key).then(() => {
        this.$buefy.toast.open({
          message: 'API key copied.',
          type: 'is-success',
        });
      });
    },
    handlePermProviderOverride(isOverride) {
      this.perm_provider_override_clicked = true;
      request
        .put(`/admin/organizations/${this.$route.params.id}`, {
          perm_provider_override: isOverride,
        })
        .finally(() => (this.perm_provider_override_clicked = null));
    },
    handleFeature(event) {
      let action = event ? 'activate' : 'deactivate';
      let actionTitle = action.charAt(0).toUpperCase() + action.slice(1);
      let msg = `<p>This will ${action} Reimbursement Estimates.</p>`;
      this.$buefy.dialog.confirm({
        title: 'Reimbursement Estimates',
        message: msg,
        confirmText: actionTitle,
        type: 'is-success',
        onCancel: () => {
          // This ensures the switch returns to its original position on cancel.
          this.loading = true;
          this.getOrganization().finally(() => (this.loading = false));
        },
        onConfirm: () => {
          this.loading = true;
          request
            .put(`/admin/organizations/${this.$route.params.id}`, {
              has_override_reimbursement: event,
            })
            .then(() => {
              this.$buefy.toast.open({
                message: `You have ${action}d Reimbursements.`,
                type: 'is-success',
              });
            });
        },
      });
    },
    handleBlanketVOBOverride(event) {
      if (!event) {
        // Disabling override - simple confirmation
        let msg = '<p>This will deactivate Blanket VOB Override.</p>';

        this.$buefy.dialog.confirm({
          title: 'Blanket VOB Override',
          message: msg,
          confirmText: 'Deactivate',
          type: 'is-danger',
          onCancel: () => {
            this.loading = true;
            this.getOrganization().finally(() => (this.loading = false));
          },
          onConfirm: () => {
            this.loading = true;
            request
              .put(`/admin/organizations/${this.$route.params.id}`, {
                has_override_blanket_vob: false,
              })
              .then(() => {
                this.$buefy.toast.open({
                  message: 'You have deactivated Blanket VOB Override.',
                  type: 'is-success',
                });
                return this.getOrganization();
              })
              .catch((error) => {
                this.$buefy.toast.open({
                  message: error.response?.data?.error || 'Failed to update override',
                  type: 'is-danger',
                });
                return this.getOrganization();
              })
              .finally(() => {
                this.loading = false;
              });
          },
        });
        return;
      }

      // Activating override - show form with fields
      this.$buefy.dialog.prompt({
        title: 'Activate Blanket VOB Override',
        message: `
          <p class="mb-4">Configure the override settings for this organization:</p>
          <div class="field">
            <label class="label">Usage Limit (0 = unlimited)</label>
            <div class="control">
              <input id="vob-limit-input" class="input" type="number" min="0" value="0" placeholder="0">
            </div>
            <p class="help">Number of blanket VOBs allowed during this override period</p>
          </div>
          <div class="field mt-4">
            <label class="label">Expiration Date (Optional)</label>
            <div class="control">
              <input id="vob-date-input" class="input" type="date" value="" min="${new Date().toISOString().split('T')[0]}">
            </div>
            <p class="help">Leave empty for no expiration</p>
          </div>
        `,
        confirmText: 'Activate',
        cancelText: 'Cancel',
        trapFocus: true,
        customClass: 'blanket-vob-override-modal',
        inputAttrs: {
          type: 'hidden',
        },
        onConfirm: () => {
          this.loading = true;

          // Get values from the form
          const limitInput = document.getElementById('vob-limit-input');
          const dateInput = document.getElementById('vob-date-input');

          const limit = parseInt(limitInput?.value || '0', 10);
          const dateValue = dateInput?.value;

          const payload = {
            has_override_blanket_vob: true,
            override_blanket_vob_limit: limit,
          };

          if (dateValue) {
            // Convert to ISO string for the API
            const selectedDate = new Date(dateValue);
            selectedDate.setHours(23, 59, 59, 999); // End of day
            payload.override_blanket_vob_ends_at = selectedDate.toISOString();
          }

          request
            .put(`/admin/organizations/${this.$route.params.id}`, payload)
            .then(() => {
              this.$buefy.toast.open({
                message: 'You have activated Blanket VOB Override.',
                type: 'is-success',
              });
              return this.getOrganization();
            })
            .catch((error) => {
              this.$buefy.toast.open({
                message: error.response?.data?.error || 'Failed to activate override',
                type: 'is-danger',
              });
              return this.getOrganization();
            })
            .finally(() => {
              this.loading = false;
            });
        },
        onCancel: () => {
          // Reset the switch
          this.loading = true;
          this.getOrganization().finally(() => (this.loading = false));
        },
      });
    },
  },
};
</script>

<style scoped>
::v-deep .blanket-vob-override-modal .modal-card-head {
  background-color: #7957d5;
  color: white;
}

::v-deep .blanket-vob-override-modal .modal-card-title {
  color: white;
}

::v-deep .blanket-vob-override-modal .delete {
  background-color: rgba(255, 255, 255, 0.3);
}

::v-deep .blanket-vob-override-modal .delete:hover {
  background-color: rgba(255, 255, 255, 0.5);
}

.api-key-value {
  font-family: monospace;
  word-break: break-all;
}
</style>
